import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { RecommendedToursViewModel } from '../models/RecommendedToursViewModel';
import type { TourDetailViewModel } from '../models/TourDetailViewModel';
import type { TourExtraServiceSearchViewModel } from '../models/TourExtraServiceSearchViewModel';
import type { TourListViewModel } from '../models/TourListViewModel';
import type { TourPackageDetailDeparturePointViewModel } from '../models/TourPackageDetailDeparturePointViewModel';
import type { TourPackageDetailViewModel } from '../models/TourPackageDetailViewModel';
import type { TourPassportsViewModel } from '../models/TourPassportsViewModel';
import type { TourPreCalculateRequestViewModel } from '../models/TourPreCalculateRequestViewModel';
import type { TourPreCalculateViewModel } from '../models/TourPreCalculateViewModel';
import type { TourRecommendationsRequestViewModel } from '../models/TourRecommendationsRequestViewModel';
import type { TourSearchViewModel } from '../models/TourSearchViewModel';
import type { TourSorting } from '../models/TourSorting';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class ToursService {
  /**
   * @deprecated
   * Search
   * Searches tours
   * @returns TourSearchViewModel OK
   * @throws ApiError
   */
  public static async searchTour(
    {
      s,
    }: {
      /**
       * Search terms
       */
      s: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<TourSearchViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<TourSearchViewModel>>({
      method: 'GET',
      url: `/tours/search`,
      query: {
        ...(s ? { s: s } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * List
   * Gets all tours by slug
   * @returns TourListViewModel OK
   * @throws ApiError
   */
  public static async listTours(
    {
      keywords,
      slug,
      sort,
      region,
      theme,
      feature,
      departure,
      transportation,
      visa,
      specialPeriod,
      date,
      status,
      priceMin,
      priceMax,
      durationMin,
      durationMax,
      page,
      size,
    }: {
      keywords?: string;
      slug?: string;
      /**
       * Sort: popularity, priceasc or pricedesc, Default: popularity
       */
      sort?: TourSorting;
      region?: Array<number>;
      theme?: Array<number>;
      feature?: Array<number>;
      departure?: Array<number>;
      transportation?: Array<number>;
      visa?: Array<number>;
      specialPeriod?: Array<number>;
      date?: Array<number>;
      status?: boolean;
      priceMin?: number;
      priceMax?: number;
      durationMin?: number;
      durationMax?: number;
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<TourListViewModel>({
      method: 'GET',
      url: `/tours`,
      query: {
        ...(keywords ? { keywords: keywords } : {}),
        ...(slug ? { slug: slug } : {}),
        ...(sort ? { sort: sort } : {}),
        ...(region ? { region: region } : {}),
        ...(theme ? { theme: theme } : {}),
        ...(feature ? { feature: feature } : {}),
        ...(departure ? { departure: departure } : {}),
        ...(transportation ? { transportation: transportation } : {}),
        ...(visa ? { visa: visa } : {}),
        ...(specialPeriod ? { specialPeriod: specialPeriod } : {}),
        ...(date ? { date: date } : {}),
        ...(departure ? { departure: departure } : {}),
        ...(transportation ? { transportation: transportation } : {}),
        ...(visa ? { visa: visa } : {}),
        ...(specialPeriod ? { specialPeriod: specialPeriod } : {}),
        ...(status ? { status: status } : {}),
        ...(priceMin ? { priceMin: priceMin } : {}),
        ...(priceMax ? { priceMax: priceMax } : {}),
        ...(durationMin ? { durationMin: durationMin } : {}),
        ...(durationMax ? { durationMax: durationMax } : {}),
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Detail
   * Gets a tour details by slug
   * @returns TourDetailViewModel OK
   * @throws ApiError
   */
  public static async getTour(
    {
      slug,
      date,
      basketId,
    }: {
      /**
       * Tour Slug
       */
      slug: string;
      /**
       * Tour Dates
       */
      date?: Array<number>;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<TourDetailViewModel>({
      method: 'GET',
      url: `/tours/${slug}`,
      path: {
        ...(slug ? { slug: slug } : {}),
      },
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(date ? { date: date } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Precalculate
   * Precalculate  tour package price
   * @returns TourPreCalculateViewModel OK
   * @throws ApiError
   */
  public static async tourPrecalculate(
    {
      requestBody,
    }: {
      /**
       * Tour precalculate request model
       */
      requestBody?: TourPreCalculateRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourPreCalculateViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<TourPreCalculateViewModel>({
      method: 'POST',
      url: `/tours/precalculate`,
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Package Detail
   * Gets tour package detail
   * @returns TourPackageDetailViewModel OK
   * @throws ApiError
   */
  public static async tourPackageDetail(
    {
      tourPackageId,
      tourPackageDetailId,
      salesDate,
      checkInDate,
    }: {
      /**
       * Tour package id
       */
      tourPackageId: number;
      /**
       * Tour package detail id
       */
      tourPackageDetailId?: number;
      /**
       * Sales date
       */
      salesDate?: string;
      /**
       * Check-in date
       */
      checkInDate?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourPackageDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<TourPackageDetailViewModel>({
      method: 'GET',
      url: `/tours/get-package`,
      query: {
        ...(tourPackageId ? { tourPackageId: tourPackageId } : {}),
        ...(tourPackageDetailId
          ? { tourPackageDetailId: tourPackageDetailId }
          : {}),
        ...(salesDate ? { salesDate: salesDate } : {}),
        ...(checkInDate ? { checkInDate: checkInDate } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Departure Points
   * Gets departure points by tour package id and detail id
   * @returns TourPackageDetailDeparturePointViewModel OK
   * @throws ApiError
   */
  public static async departurePoints(
    {
      detailId,
    }: {
      detailId: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<TourPackageDetailDeparturePointViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<
      Array<TourPackageDetailDeparturePointViewModel>
    >({
      method: 'GET',
      url: `/tours/departures/${detailId}`,
      path: {
        ...(detailId ? { detailId: detailId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Passports
   * Gets passport types
   * @returns TourPassportsViewModel OK
   * @throws ApiError
   */
  public static async passports(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourPassportsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<TourPassportsViewModel>({
      method: 'GET',
      url: `/tours/passports`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Extra Services (Search)
   * Gets extra services for tour
   * @returns TourExtraServiceSearchViewModel OK
   * @throws ApiError
   */
  public static async extraServiceSearch(
    {
      saleDate,
      startDate,
      endDate,
      tourId,
      numberofPeople,
    }: {
      saleDate?: string;
      startDate?: string;
      endDate?: string;
      tourId?: number;
      numberofPeople?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourExtraServiceSearchViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<TourExtraServiceSearchViewModel>({
      method: 'GET',
      url: `/tours/search/extraservice`,
      query: {
        ...(saleDate ? { saleDate: saleDate } : {}),
        ...(startDate ? { startDate: startDate } : {}),
        ...(endDate ? { endDate: endDate } : {}),
        ...(tourId ? { tourId: tourId } : {}),
        ...(numberofPeople ? { numberofPeople: numberofPeople } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Recommendations (Tour)
   * Gets recommendations for tours.
   * @returns RecommendedToursViewModel OK
   * @throws ApiError
   */
  public static async tourRecommendations(
    {
      requestBody,
    }: {
      requestBody?: TourRecommendationsRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<RecommendedToursViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<RecommendedToursViewModel>({
      method: 'POST',
      url: `/tours/recommendations`,
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
